import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/features/auth/useAuth'
import { useFeatureFlag } from '@/features/feature-flags/useFeatureFlag'
import { labRequest } from '../rhyme-lab/labClient'
import './writing-daily.css'

const WritingDailyContext = createContext(null)
export const useWritingDaily = () => useContext(WritingDailyContext)

export default function WritingDailyProvider({ children }) {
  const { user } = useAuth()
  const labEnabled = useFeatureFlag('training.rhyme-lab')
  const trainingEnabled = useFeatureFlag('dashboard.training')
  const enabled = labEnabled && trainingEnabled
  return <UserWritingDailyProvider key={user?.id ?? 'signed-out'} userId={user?.id} enabled={!!user?.id && enabled}>{children}</UserWritingDailyProvider>
}

function UserWritingDailyProvider({ children, userId, enabled }) {
  const { pathname } = useLocation()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const requestId = useRef(0)
  const inFlight = useRef(false)
  const writing = useRef(false)
  const channel = useRef(null)
  const queued = useRef(false)
  const mounted = useRef(true)
  const enabledRef = useRef(enabled); enabledRef.current = enabled
  const refreshRef = useRef(null)
  const refresh = useCallback(async () => {
    if (!enabledRef.current) return
    if (inFlight.current || writing.current) { queued.current = true; return }
    inFlight.current = true
    const id = ++requestId.current
    try {
      const result = await labRequest('/api/rhyme-lab/writing/daily')
      if (mounted.current && id === requestId.current) { setData(result); setError('') }
    } catch (e) { if (mounted.current && id === requestId.current) setError(e.message) }
    finally {
      inFlight.current = false
      if (queued.current && mounted.current && enabledRef.current && !writing.current) { queued.current = false; void refreshRef.current() }
    }
  }, [])
  refreshRef.current = refresh
  useEffect(() => { if (!enabled) { ++requestId.current; setData(null); setError('') } else void refresh() }, [enabled, pathname, refresh])
  useEffect(() => {
    mounted.current = true
    if (!enabled) return
    const changed = () => { void refresh(); channel.current?.postMessage('refresh') }
    const visible = () => { if (document.visibilityState !== 'hidden') void refresh() }
    if (typeof BroadcastChannel === 'function') {
      channel.current = new BroadcastChannel(`wurds-writing-daily:${userId}`)
      channel.current.onmessage = () => { void refresh() }
    }
    window.addEventListener('writing-daily-update', changed)
    window.addEventListener('focus', visible)
    document.addEventListener('visibilitychange', visible)
    const timer = setInterval(visible, 60000)
    return () => {
      mounted.current = false
      window.removeEventListener('writing-daily-update', changed)
      window.removeEventListener('focus', visible)
      document.removeEventListener('visibilitychange', visible)
      clearInterval(timer); channel.current?.close(); channel.current = null
    }
  }, [enabled, userId, refresh])
  useEffect(() => {
    if (!enabled || !data?.nextMidnight) return
    const delay = new Date(data.nextMidnight).getTime() - Date.now() + 100
    if (delay <= 0) return
    const timer = setTimeout(() => { void refresh() }, Math.min(delay, 2147483647))
    return () => clearTimeout(timer)
  }, [enabled, data?.nextMidnight, refresh])
  const save = async (settings) => {
    if (writing.current) throw new Error('Your settings are still saving.')
    writing.current = true; setSaving(true)
    const id = ++requestId.current
    try {
      const result = await labRequest('/api/rhyme-lab/writing/daily/settings', settings, 'PUT')
      if (mounted.current && id === requestId.current) { setData(result); setError('') }
      channel.current?.postMessage('refresh')
      return result
    } finally {
      writing.current = false
      if (mounted.current) setSaving(false)
      if (queued.current && mounted.current && enabledRef.current && !inFlight.current) { queued.current = false; void refreshRef.current() }
    }
  }
  return <WritingDailyContext.Provider value={enabled ? { data, error, refresh, save, saving } : null}>{children}</WritingDailyContext.Provider>
}
